import XDate from 'xdate';
import {SET_EVITE_EVENT} from '~/common/fabric/reducers/eviteEvent/constants';
import {SET_WIDGETS} from '~/common/fabric/reducers/widgets/constants';
import {formatTimezoneOffset, removeNestedProperty} from '~/common/utils';
import {
  selectSelectedLiner,
  selectSelectedStamp,
  selectSelectedBackground,
  selectSelectedEnvelope,
  selectSelectedOverlay,
  selectSelectedReveal,
} from '~/CreateFabricInvitation/reducers/fabricDesign/selectors';
import {updateEventIdInPathname} from '~/CreateFabricInvitation/utils/save';
import {fetchInvitationSummary} from '~/ViewInvitation/reducers/apiActions';
import {selectHostIds, selectPendingHostIds} from '~/common/fabric/reducers/eviteEvent/selectors';
import {toastAlert} from '~library/atoms/Toast';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';
import {eventApi} from '~/CreateFabricInvitation/api/endpoints/event';
// TODO: we are commenting this out for a temporary hot fix (EVT-27156). Uncomment when EVT-27159 is fixed
// import isEqual from 'lodash/isEqual';
// import {setEventToBackendSnapshot} from '~/common/fabric/Snapshot/reducers';
// import {selectEventToBackendSnapshot} from '~/common/fabric/Snapshot/reducers/selector';

const toast = (message, id) => {
  if (!document.getElementById(id)) {
    toastAlert(message, {
      type: 'error',
      icon: <AlertLineIcon ratio={0.65} />,
      position: 'top-right',
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      progress: undefined,
      id: id || 'api-error',
    });
  }
};

const twoWeeksFromNow = () => {
  const future = new XDate().addWeeks(2).setHours(18).setMinutes(0).setSeconds(0);
  return future.toString('i').slice(0, 16) + formatTimezoneOffset();
};

export const stateToBackendEventRepresentation = (state) => {
  const {details, giftingOptions, fabricTemplate, eviteEvent, fabricDesign, coverData} = state;
  const background = selectSelectedBackground(state);
  const overlay = selectSelectedOverlay(state);
  const reveal = selectSelectedReveal(state);
  const stamp = selectSelectedStamp(state);
  const liner = selectSelectedLiner(state);
  const envelope = selectSelectedEnvelope(state);
  const {widgets} = state;
  const {
    gift_registry_name: giftRegistryName,
    gift_registry_note: giftRegistryNote,
    gift_registry_urls: giftRegistryUrls,
    gift_registry_type: giftRegistryType,
    pledgeling_data: pledgelingData,
  } = giftingOptions;

  const hostIDs = selectHostIds(state);
  const pendingHostIDs = selectPendingHostIds(state);

  if (!fabricDesign.fabricArt?.objects?.length) {
    window.Raven.setTagsContext({key: 'eventTime', value: new Date()});
  }

  const eventTypeSource = window.dataLayer?.find(
    (obj) => 'event_type_source' in obj
  )?.event_type_source;

  const event = {
    ...eviteEvent,
    host_ids: hostIDs,
    pending_host_ids: pendingHostIDs,
    origin: 21,
    status: eviteEvent.status || 'draft',
    template_name: fabricTemplate.templateName,
    fabric_design_data: fabricDesign.fabricArt,
    host_name: details.host,
    phone_number: details.phoneNumber,
    event_type: details.type,
    cohost_name1: details.cohost.name,
    cohost_email1: details.cohost.email,
    guests_of_honor: {...details.guestsOfHonor},
    location_name: details.location,
    street_address: details.address.street,
    city: details.address.city,
    state: details.address.state,
    zip_code: details.address.zip,
    title: details.title,
    message: details.notes,
    start_datetime: details.start
      ? XDate(details.start).toString('i').slice(0, 16) + formatTimezoneOffset()
      : twoWeeksFromNow(),
    end_datetime:
      details.end && XDate(details.end).toString('i').slice(0, 16) + formatTimezoneOffset(),
    stop_datetime:
      details.stop && XDate(details.stop).toString('i').slice(0, 16) + formatTimezoneOffset(),
    known_timezone_name: details.known_timezone_name || coverData.known_timezone_name,
    known_timezone_abbr: details.known_timezone_abbr || coverData.known_timezone_abbr,
    // send_on: ,
    gift_registry_urls: giftRegistryUrls,
    gift_registry_name: giftRegistryName,
    gift_registry_note: giftRegistryNote,
    gift_registry_type: giftRegistryType,
    rsvp_off: details.rsvp_off,
    gift_keywords: null,
    gift_list_url: null,
    wishlist: null,
    options: {
      ...eviteEvent.options,
      ...giftingOptions.options,
      event_option_allow_invite_more: false, // This will no longer be supported in Fabric invitations
      // Remove rendered data so we don't overwrite what is on the server getting set asynchronously
      rendered_image_filename: undefined,
      rendered_image_gs_key: undefined,
      rendered_image_url: undefined,
      rendered_text_image_url: undefined,
    },
    is_fabric_premium: details.isPremium,
    pledgeling_data: pledgelingData,
    pledgeling_new_api: true,
    // fields to not send back
    guessing_games_data: undefined,
    sent_on: undefined,
    last_updated: undefined,
    date_created: undefined,
    event_type_source: eventTypeSource,
  };

  event.options.design = {...(event.options.design || {})};
  event.used_as = eviteEvent.id ? event.used_as : fabricTemplate.usedAs;
  const {design} = event.options;
  if (envelope) {
    design.envelope = envelope.id;
    design.envelope_flap_down = envelope.flap_down_image;
    design.envelope_flap_up = envelope.blank;
    design.envelope_front = envelope.image;
    design.name_style = {
      // Text style for names on front of envelopes
      align: 'center',
      color: envelope.text_color,
      font: 'Financier',
      position: {x: 15, x2: 605, y: 225, y2: 465},
      size: 48,
    };

    if (liner) {
      design.liner = liner.id;
      liner.media.forEach((media) => {
        if (media.label === 'image') design.liner_image = media.url;
      });
    }
    if (stamp) {
      design.stamp = stamp.id;
      stamp.media.forEach((media) => {
        if (media.label === 'image') design.stamp_image = media.url;
      });
    }
    if (background) {
      design.background = background.id;
      background.media.forEach((media) => {
        if (media.label === 'mobile') design.background_image_mobile = media.url;
        else if (media.label === 'desktop') design.background_image_desktop = media.url;
      });
      design.background_color = null;
    } else {
      design.background = null;
      design.background_image_mobile = null;
      design.background_image_desktop = null;
      design.background_color = fabricDesign.backgroundColor;
    }
  }
  if (overlay) {
    design.overlay_id = overlay.id;
    overlay.media.forEach((media) => {
      if (media.label === 'mobile_vp9') design.overlay_video_mobile_vp9 = media.url;
      else if (media.label === 'desktop_vp9') design.overlay_video_desktop_vp9 = media.url;
      else if (media.label === 'mobile_h265') design.overlay_video_mobile_h265 = media.url;
      else if (media.label === 'desktop_h265') design.overlay_video_desktop_h265 = media.url;
    });
  } else {
    design.overlay_id = '';
    design.overlay_video_mobile_vp9 = '';
    design.overlay_video_desktop_vp9 = '';
    design.overlay_video_mobile_h265 = '';
    design.overlay_video_desktop_h265 = '';
  }
  if (reveal) {
    design.reveal_id = reveal.id;
    reveal.media.forEach((media) => {
      if (media.label === 'mobile_vp9') design.reveal_video_mobile_vp9 = media.url;
      else if (media.label === 'desktop_vp9') design.reveal_video_desktop_vp9 = media.url;
      else if (media.label === 'mobile_h265') design.reveal_video_mobile_h265 = media.url;
      else if (media.label === 'desktop_h265') design.reveal_video_desktop_h265 = media.url;
    });
  } else {
    design.reveal_id = '';
    design.reveal_video_mobile_vp9 = '';
    design.reveal_video_desktop_vp9 = '';
    design.reveal_video_mobile_h265 = '';
    design.reveal_video_desktop_h265 = '';
  }
  design.envelope_fabric_stickers = fabricDesign.envelopeStickers;

  // We don't need to send "created" fields to the BE since it does not make sense to update them
  removeNestedProperty(widgets, 'created');

  if (!event.tracking_data)
    event.tracking_data = {gallery_category: details.tracking_gallery_category};

  window.saveUTMToObj(event);

  return {event, widgets};
};

export const getSaveEventURL = (eventId) =>
  eventId
    ? new URL(`/ajax/event/${eventId}/update/`, window.location.origin)
    : new URL('/ajax/event/create/', window.location.origin);

export const createOrUpdateEvent = async (url, body) =>
  evite.fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'X-CSRFToken': evite.cookie('csrftoken'),
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

export const saveEventThunk =
  (saveToBackend = true, delayFetchSummaryTime = 0) =>
  async (dispatch, getState) => {
    const state = getState();
    const {
      eviteEvent,
      details: {inEditMode, guestsOfHonor},
    } = state;
    const eventId = eviteEvent.id;
    const url = getSaveEventURL(eventId);
    const eventRepresentation = stateToBackendEventRepresentation(state);
    let saveStatus;

    if (saveToBackend) {
      let resp;
      try {
        resp = await createOrUpdateEvent(url, eventRepresentation);

        saveStatus = resp.status;
        // hack for sending the event after modifying guests of honor
        if (inEditMode && guestsOfHonor?.update) {
          await eventApi.endpoints.send.initiate(state.eviteEvent.id);
        }
      } catch (e) {
        toast('Unable to save event, please check your internet connection', 'nav-err');
      }
      if (!resp?.ok) {
        const {message} = await resp.json();
        evite.error('Failed to save event', new Error(message));
        toast(message || 'Unable to save event, please contact support', 'nav-err');
        throw message;
      }
      const results = await resp.json();

      if (!eventId) updateEventIdInPathname(results.event.id);
      window.evite_event = results.event;
      dispatch({type: SET_EVITE_EVENT, payload: results.event});
      dispatch({type: SET_WIDGETS, payload: results});
      // TODO: we are commenting this out for a temporary hot fix (EVT-27156). Uncomment when EVT-27159 is fixed
      // dispatch(
      //     setEventToBackendSnapshot(stateToBackendEventRepresentation(getState(), sendInvitations))
      // );
    } else {
      window.evite_event = eventRepresentation.event;
      dispatch({type: SET_EVITE_EVENT, payload: eventRepresentation.event});
      // TODO: we are commenting this out for a temporary hot fix (EVT-27156). Uncomment when EVT-27159 is fixed
      // dispatch(
      //     setEventToBackendSnapshot(stateToBackendEventRepresentation(getState(), sendInvitations))
      // );
    }

    if (window.evite_event) {
      if (delayFetchSummaryTime > 0) {
        await new Promise((resolve) => {
          setTimeout(resolve, delayFetchSummaryTime);
        });
      }
      await dispatch(fetchInvitationSummary(window.evite_event.id));
    }
    return saveStatus;
  };
